import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

// State input that stays in sync with a URL query parameter.
//
// Why a hook (not just useState(() => urlParam)):
//   AdminSearch's Pages-section live-search lets admins jump to a
//   pre-filtered list page (e.g. /admin/warehouse/locations?search=NP-A-1)
//   from the global header. When the user is ALREADY on that route, React
//   Router does a soft navigation — the component instance survives, only
//   the URL changes. A `useState(() => urlParam())` initializer runs once
//   at first mount and never sees the new URL, so the page's search field
//   stays empty even though the URL says otherwise.
//
// This hook treats the URL as the source of truth on first mount AND on
// later URL changes. User typing updates local state but does NOT push back
// to the URL — that would spam history per keystroke. The trade-off is that
// after the user types, refresh re-applies the URL value (if any).
export const useUrlSearchParam = (key: string): [string, (v: string) => void] => {
    const [params] = useSearchParams();
    const fromUrl = params.get(key) ?? "";
    const [value, setValue] = useState(fromUrl);
    useEffect(() => { setValue(fromUrl); }, [fromUrl]);
    return [value, setValue];
};
